import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Ad,
    AppContent,
    ErrorBoundary,
    MEDIA_BREAKPOINTS,
    Search,
    SearchPageFeed,
    Section,
    TextStripe,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import {
    getAdSlotId,
    getAdSlotOnSuccess,
} from '../../store/config/config.selectors';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import {
    getMetadataDescription,
    getMetadataImage,
    getMetadataTitle,
    getSectionArticlesByKey,
    getSectionPaginationUrlByKey,
} from '../../store/template/searchPage/searchPageKampala/searchPageKampala.selector';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';

interface SearchPageProps {
    store: Store<AppState, AppActions>;
    errorProvider: ErrorReporter;
}

export const SearchPageKampalaComponent: React.FunctionComponent<SearchPageProps> = ({ store, errorProvider }) => {
    useMeasureHydrateTime();
    return (
      <Page store={store} errorProvider={errorProvider}>
        <ErrorBoundary>
          <SearchPageKampalaContentConnected />
        </ErrorBoundary>
      </Page>
    );
};

const createStyle = () => StyleSheet.create({
    topSectionsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    searchWrapper: {
        display: 'flex',
        flexDirection: 'column',
        [MEDIA_BREAKPOINTS.large]: {
            padding: '10px 30px',
        },
        [MEDIA_BREAKPOINTS.medium]: {
            padding: '10px 30px',
        },
        [MEDIA_BREAKPOINTS.small]: {
            padding: '5px 20px',
        },
    },
    articlesWrapper: {
        [MEDIA_BREAKPOINTS.large]: {
            padding: '10px 30px',
        },
        [MEDIA_BREAKPOINTS.medium]: {
            padding: '10px 30px',
        },
        [MEDIA_BREAKPOINTS.small]: {
            padding: '5px 20px',
        },
    },
    noResultsText: {
        color: '#212121',
        fontFamily: 'Industry',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '22px',
        padding: '20px 0px',
    },
});

export const SearchPageKampalaContentConnected: React.FunctionComponent = () => {
    const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION));
    const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
    const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
    const metaTitle = useSelector(getMetadataTitle);
    const metaDescription = useSelector(getMetadataDescription);
    const pageImage = useSelector(getMetadataImage);

    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const isTopSectionArticles = topSectionArticles && topSectionArticles.length > 0;
    const showTextStripe = !!searchQuery && isTopSectionArticles;
    const noResults = !!searchQuery && !isTopSectionArticles;
    const style = createStyle();

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        setSearchQuery(searchParams.get('query'));
    }, []);

    const onSearchClicked = (searchValue: string) => {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set('query', searchValue);
        window.location.search = queryParams.toString();
    };

    return (
      <React.Fragment>
        <MetaTags noIndex title={metaTitle} description={metaDescription} pageImage={pageImage} />
        <HeadScripts />
        <Theme>
          <AppContent>
            <NavigationLayout mainWrapperConfigThemeKey="mainContent">
              <div className={css(style.topSectionsWrapper)}>
                <Section themeConfigurationPropKey="firstSectionMaxWidth">
                  <div className={css(style.searchWrapper)}>
                    <Search onSearchClick={onSearchClicked} />
                    {noResults && <span className={css(style.noResultsText)}>No results found</span>}
                  </div>
                </Section>
                {showTextStripe && <TextStripe text={searchQuery || ''} />}
                <Section themeConfigurationPropKey="firstSectionMaxWidth">
                  <div className={css(style.articlesWrapper)}>
                    <SearchPageFeed articles={topSectionArticles} />
                  </div>
                </Section>
              </div>
              <SeeMorePostsButtonConnected />
            </NavigationLayout>
            <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
          </AppContent>
        </Theme>
      </React.Fragment>
    );
};

export const SeeMorePostsButtonConnected: React.FunctionComponent = () => {
    const shouldDisplay = !!useSelector(getSectionPaginationUrlByKey(SECTION_NAMES.TOP_SECTION));
    const onClick = useFetchNextPosts(SECTION_NAMES.TOP_SECTION, getSectionPaginationUrlByKey);
    return <LoadMoreButton {...{ onClick, shouldDisplay, text: 'SEE MORE' }} />;
};
